// third party libs
import React, { useContext, useReducer } from 'react';

// local components and data
import { isSessionActive, getUserSub } from '@sgp/cognito';
import { isSsoSessionActive, getSsoUserSub } from '@sgp/sso';
import { startTokenRefreshScheduler, stopTokenRefreshScheduler } from '@sgp/cognito/refresh';

// Seed the login state from an existing session (Cognito or SSO) so a page
// refresh keeps the user authenticated. The two flows use separate stores, so
// whichever one is active drives the identifier.
const resolveInitialToken = () => {
  if (isSessionActive()) {
    // A Cognito session already exists (e.g. user pressed F5 after login, or the
    // app was opened from a deep link). The callback-time scheduler was lost on
    // reload, so restart it here. SSO sessions never store a Cognito refresh token,
    // so this is a no-op for them.
    startTokenRefreshScheduler();
    return getUserSub() || 'authenticated';
  }
  if (isSsoSessionActive()) {
    return getSsoUserSub() || 'authenticated';
  }
  return '';
};

const initialContext = { value: resolveInitialToken() };
const DspContext = React.createContext(initialContext);

const DspReducer = (state, action) => {
  switch (action.type) {
  case 'settoken': {
    return action.payload;
  }
  case 'setAgentInfo': {
    // Cache the resolved agent profile alongside the login token so the header
    // does not re-fetch get_user_profile on every mount. Merged into the current
    // state so the login token is preserved.
    return { ...state, agentInfo: action.payload?.agentInfo };
  }
  default: {
    // Never throw: this reducer sits above the router's ErrorBoundary, so an
    // unhandled action would unmount the whole app and leave a blank page with
    // no way to log out. Ignore the action instead.
    console.error(`Unhandled action type: ${action.type}`);
    return state;
  }
  }
};

const DspProvider = ({ children }) => {
  const [dsptoken, dspTokenDispatch] = useReducer(DspReducer, initialContext);
  let value = { dsptoken, dspTokenDispatch };
  return (
    <DspContext.Provider value={value}>
      {children}
    </DspContext.Provider>
  );
};

const DspContextConsumer = DspContext.Consumer;

const useDspContext = () => useContext(DspContext);

export { DspProvider, useDspContext, DspContextConsumer };
